import { SearchOutlined } from "@ant-design/icons";
import { theme, Tooltip } from "antd";
import { PayoutCell } from "components/UI/Table/UI/PayoutCell/PayoutCell.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { FC, MouseEvent } from "react";
import { PermissionEnum } from "types/enums.ts";
import { DrawerId } from "types/models.ts";

interface WorkspacePayoutCellProps {
    commission: App.Data.CommissionData;
}

// Shows the workspace (tenant-level) commission's payout, with a magnifying-glass icon matching
// ShortenedIdWithCopy's copy icon: value left, icon right, no button chrome, colorLink token (the
// same color antd's Text copyable icon uses) plus a tooltip. Clicking it opens the SAME drawer a
// row click already opens for a commission (DrawerId.EditCommission, gated by CommissionView) —
// just scoped to the PARENT commission's id instead of the row's own. stopPropagation keeps it
// from also triggering the row's own click handler.
export const WorkspacePayoutCell: FC<WorkspacePayoutCellProps> = ({ commission }) => {
    const drawer = useDrawer();
    const { hasPermission } = usePermission();
    const { t } = usePaldockTranslation("tables.commissions-table");
    const { token } = theme.useToken();

    if (!hasPermission(PermissionEnum.CommissionView)) {
        return <PayoutCell commission={commission} />;
    }

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        drawer.show(DrawerId.EditCommission, { [`${DrawerId.EditCommission}-id`]: commission.id });
    };

    return (
        <span className="w-full flex justify-between items-center">
            {/* Matches the row's own payout_formula column (WithPayoutFormula), which replaces
                the formatted value with 🚫 for a disabled commission. */}
            {commission.is_enabled ? <PayoutCell commission={commission} /> : <>🚫</>}
            <Tooltip title={t("columns.workspace_payout_tooltip")}>
                <button
                    type="button"
                    onClick={handleClick}
                    className="bg-transparent p-0 leading-none"
                    style={{ color: token.colorLink }}
                >
                    <SearchOutlined />
                </button>
            </Tooltip>
        </span>
    );
};
